import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import FeatureHeader from "../components/FeatureHeader";
import {
  Activity,
  ArrowLeft,
  Bell,
  Check,
  Droplets,
  Dumbbell,
  Flame,
  Footprints,
  HeartPulse,
  Medal,
  Plus,
  Moon,
  Scale,
  Sparkles,
  Timer,
  Trophy,
  Utensils,
  Waves,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import "./health-tips.css";

const MAIN_TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "statistics", label: "Stats" },
  { key: "achievements", label: "Badges" },
];

const DRAWER_STEPS = ["selection", "reminders"];

const REPEAT_OPTIONS = ["Daily", "Weekdays", "Weekends", "Custom"];
const CUSTOM_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const RANGE_TABS = ["today", "week", "month", "year"];

const MOODS = [
  { label: "Happy", emoji: "Happy" },
  { label: "Calm", emoji: "Calm" },
  { label: "Focused", emoji: "Focused" },
  { label: "Tired", emoji: "Tired" },
];

const HABITS = [
  { key: "water", title: "Drink Water", description: "Stay hydrated throughout the day.", icon: Droplets, target: 8, unit: "glasses", quickValue: 1 },
  { key: "exercise", title: "Exercise", description: "Stay active every day.", icon: Dumbbell, target: 60, unit: "mins", quickValue: 15 },
  { key: "medication", title: "Medication", description: "Never miss your medication.", icon: Bell, target: 3, unit: "doses", quickValue: 1 },
  { key: "sleep", title: "Sleep", description: "Improve your sleep routine.", icon: Moon, target: 8, unit: "hours", quickValue: 1 },
  { key: "walking", title: "Walking", description: "Reach your daily step goal.", icon: Footprints, target: 10000, unit: "steps", quickValue: 1000 },
  { key: "bloodPressure", title: "Blood Pressure", description: "Track your blood pressure.", icon: HeartPulse, target: 1, unit: "checks", quickValue: 1 },
  { key: "bloodSugar", title: "Blood Sugar", description: "Monitor your glucose level.", icon: Activity, target: 1, unit: "checks", quickValue: 1 },
  { key: "weight", title: "Weight", description: "Track body weight.", icon: Scale, target: 1, unit: "entry", quickValue: 1 },
  { key: "healthyMeals", title: "Healthy Meals", description: "Eat balanced meals.", icon: Utensils, target: 3, unit: "meals", quickValue: 1 },
  { key: "meditation", title: "Meditation", description: "Reduce stress and improve focus.", icon: Waves, target: 20, unit: "mins", quickValue: 5 },
  { key: "vitamins", title: "Vitamins", description: "Remember daily vitamins.", icon: Sparkles, target: 1, unit: "dose", quickValue: 1 },
  { key: "heartRate", title: "Heart Rate", description: "Track your heart health.", icon: HeartPulse, target: 1, unit: "check", quickValue: 1 },
  { key: "mood", title: "Mood", description: "Record your daily mood.", icon: Flame, target: 1, unit: "check-in", quickValue: 1 },
  { key: "stretching", title: "Stretching", description: "Improve flexibility.", icon: Activity, target: 20, unit: "mins", quickValue: 5 },
];

const MOTIVATIONAL_MESSAGES = {
  water: "Your body needs hydration. Drink a glass of water now.",
  exercise: "You are one workout away from a healthier you.",
  medication: "Your health comes first. It is time for your medication.",
  walking: "A short walk right now can make a big difference.",
  sleep: "Prepare for bedtime and recharge your body.",
  mood: "How are you feeling today? Take a moment to check in with yourself.",
};

const LOCAL_SCORE_WEIGHTS = {
  water: 20,
  exercise: 25,
  medication: 25,
  sleep: 15,
  walking: 10,
  mood: 5,
};

const HABIT_KEY_SET = new Set(HABITS.map((habit) => habit.key));

const EMPTY_MEDICATION_SLOT = "12:00";
const HABIT_TRACKER_DRAFT_KEY = "habitTrackerDraft";

function getDashboardDraftKey() {
  const userId = localStorage.getItem("userId") || "guest";
  return `habitTrackerDashboardDraft:${userId}`;
}

function readDashboardDraft() {
  try {
    const raw = localStorage.getItem(getDashboardDraftKey());
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeDashboardDraft(dashboard) {
  try {
    localStorage.setItem(getDashboardDraftKey(), JSON.stringify(dashboard || {}));
  } catch {
    // Ignore storage write failures.
  }
}

function readHabitTrackerDraft() {
  try {
    const raw = localStorage.getItem(HABIT_TRACKER_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeHabitTrackerDraft(selectedHabits, reminderSettings) {
  try {
    localStorage.setItem(
      HABIT_TRACKER_DRAFT_KEY,
      JSON.stringify({
        selectedHabits,
        reminderSettings,
      })
    );
  } catch {
    // Ignore storage write failures.
  }
}

function defaultReminderFor(habitKey) {
  if (habitKey === "water") {
    return {
      enabled: true,
      repeat: "Daily",
      interval: "1 hour",
      time: "09:00",
      startTime: "09:00",
      endTime: "21:00",
      customDays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    };
  }

  if (habitKey === "medication") {
    return {
      enabled: true,
      repeat: "Daily",
      time: "08:00",
      startTime: "08:00",
      endTime: "20:00",
      slots: ["08:00", "14:00", "20:00"],
      customDays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    };
  }

  if (habitKey === "sleep") {
    return {
      enabled: true,
      repeat: "Daily",
      time: "22:00",
      startTime: "20:00",
      endTime: "23:30",
      sleepReminderEnabled: true,
      sleepTime: "22:00",
      wakeReminderEnabled: true,
      wakeTime: "06:30",
      customDays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    };
  }

  return {
    enabled: true,
    repeat: "Daily",
    time: "18:30",
    startTime: "08:00",
    endTime: "20:00",
    customDays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  };
}

function toPercent(progress, target) {
  if (!target) return 0;
  return Math.min(100, Math.round((Number(progress || 0) / Number(target || 1)) * 100));
}

function scoreLabel(score) {
  if (score >= 96) return "Excellent";
  if (score >= 80) return "Great";
  if (score >= 60) return "Good";
  return "Needs Improvement";
}

function computeLocalHealthScore(habits = {}) {
  let score = 0;

  Object.entries(LOCAL_SCORE_WEIGHTS).forEach(([habitKey, weight]) => {
    const state = habits?.[habitKey];
    if (!state) return;
    const target = Number(state?.target || 0);
    const progress = Number(state?.progress || 0);
    if (target <= 0) return;
    const ratio = Math.min(1, progress / target);
    score += ratio * weight;
  });

  const value = Math.round(score);
  return {
    value,
    level: scoreLabel(value),
  };
}

const NON_ACCUMULATING_HABITS = new Set(["weight", "mood"]);

function getUpdatedAtTs(state) {
  const ts = state?.updatedAt ? new Date(state.updatedAt).getTime() : 0;
  return Number.isFinite(ts) ? ts : 0;
}

function mergeHabitMaps(baseHabits = {}, incomingHabits = {}) {
  const merged = { ...(baseHabits || {}) };

  Object.entries(incomingHabits || {}).forEach(([habitKey, incomingState]) => {
    const prevState = baseHabits?.[habitKey];
    if (!prevState) {
      merged[habitKey] = incomingState;
      return;
    }

    const prevTs = getUpdatedAtTs(prevState);
    const nextTs = getUpdatedAtTs(incomingState);

    if (NON_ACCUMULATING_HABITS.has(habitKey)) {
      merged[habitKey] = nextTs >= prevTs ? { ...prevState, ...incomingState } : prevState;
      return;
    }

    const prevProgress = Number(prevState?.progress || 0);
    const nextProgress = Number(incomingState?.progress || 0);
    const target = Number(incomingState?.target || prevState?.target || 1);
    const progress = Math.max(prevProgress, nextProgress);
    const fromNewest = nextTs >= prevTs ? { ...prevState, ...incomingState } : { ...incomingState, ...prevState };

    merged[habitKey] = {
      ...fromNewest,
      target,
      progress,
      completion: toPercent(progress, target),
      updatedAt: nextTs >= prevTs ? (incomingState?.updatedAt || prevState?.updatedAt) : (prevState?.updatedAt || incomingState?.updatedAt),
    };
  });

  return merged;
}

function ProgressRing({ value }) {
  const radius = 90;
  const stroke = 14;
  const normalized = 2 * Math.PI * radius;
  const offset = normalized * (1 - value / 100);

  return (
    <div className="habit-progress-ring-wrap">
      <svg width="210" height="210" viewBox="0 0 210 210">
        <defs>
          <linearGradient id="habit-ring-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#b56bff" />
            <stop offset="100%" stopColor="#7f00ff" />
          </linearGradient>
        </defs>
        <circle cx="105" cy="105" r={radius} fill="none" stroke="#ece6f8" strokeWidth={stroke} />
        <motion.circle
          cx="105"
          cy="105"
          r={radius}
          fill="none"
          stroke="url(#habit-ring-gradient)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={normalized}
          initial={{ strokeDashoffset: normalized }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          transform="rotate(-90 105 105)"
        />
      </svg>

      <div className="habit-ring-center">
        <div>
          <h2 className="mb-1" style={{ fontSize: "2rem", fontWeight: 700 }}>{value}%</h2>
          <p className="mb-0" style={{ color: "#7d7393", fontSize: "0.8rem" }}>Today's Progress</p>
        </div>
      </div>
    </div>
  );
}

function SkeletonBlock({ height = 120, className = "" }) {
  return <div className={`habit-skeleton ${className}`.trim()} style={{ height }} />;
}

function formatTime(input) {
  if (!input) return "--";
  const [hourStr = "00", minStr = "00"] = String(input).split(":");
  const hour = Number(hourStr);
  const minute = Number(minStr);
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:${String(minute).padStart(2, "0")} ${period}`;
}

function buildLocalStats(range, selectedHabits, habitsMap) {
  const safeHabits = Array.isArray(selectedHabits) ? selectedHabits : [];
  const daysInRangeByType = {
    today: 1,
    week: 7,
    month: 30,
    year: 365,
  };

  const habits = safeHabits
    .map((habitKey) => {
      const habitMeta = HABITS.find((habit) => habit.key === habitKey);
      if (!habitMeta) return null;

      const state = habitsMap?.[habitKey] || {};
      const target = Number(state.target || habitMeta.target || 1);
      const progress = Number(state.progress || 0);
      const completionPercentage = toPercent(progress, target);
      const completedToday = completionPercentage >= 100;

      return {
        key: habitKey,
        title: habitMeta.title,
        completionPercentage,
        dailyAverage: completionPercentage,
        longestStreak: completedToday ? 1 : 0,
        currentStreak: completedToday ? 1 : 0,
        missedDays: completedToday ? 0 : 1,
        history: [
          {
            dateKey: new Date().toISOString().slice(0, 10),
            completion: completionPercentage,
            completed: completedToday,
          },
        ],
      };
    })
    .filter(Boolean);

  return {
    range,
    daysInRange: daysInRangeByType[range] || 1,
    habits,
  };
}

function buildLocalAchievements(todayScore, timelineLength) {
  const completedDays = todayScore >= 80 ? 1 : 0;
  const excellentDays = todayScore >= 96 ? 1 : 0;
  const longestStreak = todayScore >= 80 ? 1 : 0;
  const currentStreak = todayScore >= 80 ? 1 : 0;
  const firstDayProgress = timelineLength > 0 ? 1 : 0;

  const badges = [
    {
      id: "first-day",
      title: "First Day",
      icon: "flame",
      target: 1,
      progress: Math.min(1, firstDayProgress),
    },
    {
      id: "streak-7",
      title: "7 Day Streak",
      icon: "flame",
      target: 7,
      progress: Math.min(7, longestStreak),
    },
    {
      id: "streak-30",
      title: "30 Day Streak",
      icon: "flame",
      target: 30,
      progress: Math.min(30, longestStreak),
    },
    {
      id: "silver",
      title: "Silver Health Champion",
      icon: "medal",
      target: 7,
      progress: Math.min(7, completedDays),
    },
    {
      id: "gold",
      title: "Gold Health Champion",
      icon: "trophy",
      target: 14,
      progress: Math.min(14, completedDays),
    },
    {
      id: "diamond",
      title: "Diamond Champion",
      icon: "gem",
      target: 30,
      progress: Math.min(30, excellentDays),
    },
  ].map((badge) => ({
    ...badge,
    unlocked: badge.progress >= badge.target,
    percentage: Math.round((badge.progress / badge.target) * 100),
  }));

  return {
    currentStreak,
    longestStreak,
    badges,
  };
}

export default function HealthTipsPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth() || {};
  const [activeStep, setActiveStep] = useState("dashboard");
  const [drawerStep, setDrawerStep] = useState("selection");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedHabits, setSelectedHabits] = useState([]);
  const [reminderSettings, setReminderSettings] = useState({});
  const [dashboard, setDashboard] = useState(null);
  const [stats, setStats] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [recentTips, setRecentTips] = useState([]);
  const [statsRange, setStatsRange] = useState("week");
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingAchievements, setLoadingAchievements] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [habitTrackerUnavailable, setHabitTrackerUnavailable] = useState(false);

  const userName = useMemo(() => {
    const fromContext = [
      profile?.fullName,
      profile?.name,
      user?.fullName,
      user?.name,
      user?.firstName,
    ].find((value) => typeof value === "string" && value.trim().length > 0);

    if (fromContext) {
      return fromContext.trim().split(/\s+/)[0];
    }

    try {
      const raw = localStorage.getItem("user");
      const parsed = raw ? JSON.parse(raw) : null;
      const fallback = [
        parsed?.fullName,
        parsed?.name,
        parsed?.firstName,
      ].find((value) => typeof value === "string" && value.trim().length > 0);

      if (fallback) {
        return fallback.trim().split(/\s+/)[0];
      }
    } catch {
      // Ignore local storage parse failures and use default name.
    }

    return "Guest";
  }, [profile, user]);
  const selectedHabitItems = useMemo(
    () => HABITS.filter((habit) => selectedHabits.includes(habit.key)),
    [selectedHabits]
  );
  const trackedHabitKeys = useMemo(
    () => Object.keys(dashboard?.habits || {}).filter((key) => HABIT_KEY_SET.has(key)),
    [dashboard]
  );
  const dashboardHabitItems = useMemo(() => {
    const keys = new Set([...selectedHabits, ...trackedHabitKeys]);
    return HABITS.filter((habit) => keys.has(habit.key));
  }, [selectedHabits, trackedHabitKeys]);

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  }, []);

  const confettiPieces = useMemo(
    () =>
      Array.from({ length: 24 }).map((_, index) => ({
        id: index,
        left: `${Math.random() * 100}%`,
        background: ["#7f00ff", "#b566ff", "#9e34ff", "#5e21bf"][index % 4],
        delay: `${Math.random() * 0.6}s`,
      })),
    [showConfetti]
  );

  useEffect(() => {
    let mounted = true;

    const loadInitial = async () => {
      setLoadingConfig(true);
      try {
        const [configRes, tipsRes] = await Promise.allSettled([
          api.get("/api/habit-tracker/config"),
          api.get("/api/health-tips/recent?limit=5"),
        ]);

        if (!mounted) return;

        if (configRes.status === "fulfilled") {
          const config = configRes.value?.data?.config || {};
          const habits = Array.isArray(config.selectedHabits) ? config.selectedHabits : [];
          const settings = config.reminderSettings || {};
          const draft = readHabitTrackerDraft();
          const draftHabits = Array.isArray(draft?.selectedHabits) ? draft.selectedHabits : [];
          const draftSettings = draft?.reminderSettings && typeof draft.reminderSettings === "object"
            ? draft.reminderSettings
            : {};

          const mergedHabits = habits.length ? habits : draftHabits;
          const mergedSettings = Object.keys(settings).length ? settings : draftSettings;

          setSelectedHabits(mergedHabits);
          setReminderSettings(mergedSettings);
          setHabitTrackerUnavailable(false);

          writeHabitTrackerDraft(mergedHabits, mergedSettings);

          // Always default to dashboard; open setup drawer only for first-time users
          if (habits.length === 0) {
            setDrawerOpen(true);
          }
        } else if (configRes.reason?.response?.status === 404) {
          const draft = readHabitTrackerDraft();
          const draftHabits = Array.isArray(draft?.selectedHabits) ? draft.selectedHabits : [];
          const draftSettings = draft?.reminderSettings && typeof draft.reminderSettings === "object"
            ? draft.reminderSettings
            : {};

          if (draftHabits.length) {
            setSelectedHabits(draftHabits);
          }
          if (Object.keys(draftSettings).length) {
            setReminderSettings(draftSettings);
          }
          setHabitTrackerUnavailable(true);
        }

        if (tipsRes.status === "fulfilled") {
          setRecentTips(Array.isArray(tipsRes.value?.data?.tips) ? tipsRes.value.data.tips : []);
        }
      } catch (_error) {
      } finally {
        if (mounted) setLoadingConfig(false);
      }
    };

    loadInitial();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (activeStep === "dashboard") {
      fetchDashboard();
    }
  }, [activeStep]);

  useEffect(() => {
    if (activeStep === "statistics") {
      fetchStats(statsRange);
    }
  }, [activeStep, statsRange]);

  useEffect(() => {
    if (activeStep === "achievements") {
      fetchAchievements();
    }
  }, [activeStep]);

  useEffect(() => {
    if (!dashboard) return;
    writeDashboardDraft(dashboard);
  }, [dashboard]);

  useEffect(() => {
    writeHabitTrackerDraft(selectedHabits, reminderSettings);
  }, [selectedHabits, reminderSettings]);

  const fetchDashboard = async () => {
    setLoadingDashboard(true);
    try {
      const [dashboardRes, todayTipRes] = await Promise.allSettled([
        api.get("/api/habit-tracker/dashboard"),
        api.get("/api/health-tips/today"),
      ]);

      const payload =
        dashboardRes.status === "fulfilled" ? dashboardRes.value?.data?.dashboard : null;
      const dashboardDraft = readDashboardDraft();
      const source = payload || dashboardDraft || {};
      const mergedHabits = mergeHabitMaps(dashboardDraft?.habits || {}, source?.habits || {});

      if (dashboardRes.status === "rejected" && dashboardRes.reason?.response?.status === 404) {
        setHabitTrackerUnavailable(true);
      }

      const tipPayload = todayTipRes.status === "fulfilled" ? todayTipRes.value?.data?.tip : null;
      const tipText = tipPayload?.content || "";

      // Always set dashboard — use empty fallback so skeletons resolve
      setDashboard({
        ...source,
        motivationalMessages: Array.isArray(source?.motivationalMessages) ? source.motivationalMessages : [],
        ashaInsight:
          source?.ashaInsight ||
          tipText ||
          "Your daily momentum is building. Keep stacking small wins to push your Health Score above 90.",
        habits: mergedHabits,
        timeline: source?.timeline || [],
        upcomingReminders: source?.upcomingReminders || [],
        healthScore: computeLocalHealthScore(mergedHabits),
      });
    } catch (_error) {
      const dashboardDraft = readDashboardDraft();
      if (dashboardDraft) {
        setDashboard({
          ...dashboardDraft,
          habits: dashboardDraft?.habits || {},
          timeline: dashboardDraft?.timeline || [],
          upcomingReminders: dashboardDraft?.upcomingReminders || [],
          healthScore: dashboardDraft?.healthScore || { value: 0 },
          motivationalMessages: Array.isArray(dashboardDraft?.motivationalMessages)
            ? dashboardDraft.motivationalMessages
            : [],
          ashaInsight:
            dashboardDraft?.ashaInsight ||
            "Start logging habits to build your daily score.",
        });
      } else {
        // Show empty dashboard on first-time error when no local draft exists.
        setDashboard({ habits: {}, timeline: [], upcomingReminders: [], healthScore: { value: 0 }, motivationalMessages: [], ashaInsight: "Start logging habits to build your daily score." });
      }
    } finally {
      setLoadingDashboard(false);
    }
  };

  const fetchStats = async (range) => {
    setLoadingStats(true);
    try {
      const res = await api.get(`/api/habit-tracker/stats?range=${range}`);
      setStats(res?.data?.stats || null);
    } catch (_error) {
      setStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchAchievements = async () => {
    setLoadingAchievements(true);
    try {
      const res = await api.get("/api/habit-tracker/achievements");
      setAchievements(res?.data?.achievements || null);
    } catch (_error) {
      setAchievements(null);
    } finally {
      setLoadingAchievements(false);
    }
  };

  const updateReminder = (habitKey, key, value) => {
    setReminderSettings((prev) => {
      return {
        ...prev,
        [habitKey]: {
          ...(prev[habitKey] || defaultReminderFor(habitKey)),
          [key]: value,
        },
      };
    });
  };

  const updateMedicationSlot = (index, value) => {
    const settings = reminderSettings.medication || defaultReminderFor("medication");
    const slots = Array.isArray(settings.slots) ? [...settings.slots] : [settings.time || "08:00"];
    slots[index] = value;
    updateReminder("medication", "slots", slots);
  };

  const addMedicationSlot = () => {
    const settings = reminderSettings.medication || defaultReminderFor("medication");
    const slots = Array.isArray(settings.slots) ? [...settings.slots] : [settings.time || "08:00"];
    if (slots.length >= 5) return;
    slots.push(EMPTY_MEDICATION_SLOT);
    updateReminder("medication", "slots", slots);
  };

  const removeMedicationSlot = (index) => {
    const settings = reminderSettings.medication || defaultReminderFor("medication");
    const slots = Array.isArray(settings.slots) ? [...settings.slots] : [settings.time || "08:00"];
    if (slots.length <= 1) return;
    slots.splice(index, 1);
    updateReminder("medication", "slots", slots);
  };

  const toggleCustomDay = (habitKey, day) => {
    const base = reminderSettings[habitKey] || defaultReminderFor(habitKey);
    const current = Array.isArray(base.customDays) ? base.customDays : [];
    const exists = current.includes(day);
    const customDays = exists ? current.filter((item) => item !== day) : [...current, day];
    updateReminder(habitKey, "customDays", customDays);
  };

  const toggleHabit = (habitKey) => {
    setSelectedHabits((prev) => {
      return prev.includes(habitKey)
        ? prev.filter((item) => item !== habitKey)
        : [...prev, habitKey];
    });
  };

  const goToReminderSetup = () => {
    const nextSettings = { ...reminderSettings };
    selectedHabits.forEach((habitKey) => {
      if (!nextSettings[habitKey]) {
        nextSettings[habitKey] = defaultReminderFor(habitKey);
      }
    });
    setReminderSettings(nextSettings);
    setDrawerStep("reminders");
  };

  const openSetupDrawer = () => {
    setDrawerStep("selection");
    setDrawerOpen(true);
  };

  const applyLocalHabitUpdate = (habitKey, value = 1, extra = {}) => {
    const habitMeta = HABITS.find((item) => item.key === habitKey);
    if (!habitMeta) return;

    const numericValue = Number(value || 0);

    setDashboard((prev) => {
      const current = prev || {};
      const currentHabits = current.habits || {};
      const currentState = currentHabits[habitKey] || {};
      const target = Number(currentState.target || habitMeta.target || 1);
      const baseProgress = Number(currentState.progress || 0);

      const nextProgress = habitKey === "weight"
        ? Math.max(0, numericValue || 1)
        : habitKey === "mood"
          ? 1
          : Math.max(0, baseProgress + numericValue);
      const nextCompletion = toPercent(nextProgress, target);

      const nextHabits = {
        ...currentHabits,
        [habitKey]: {
          key: habitKey,
          title: habitMeta.title,
          unit: habitMeta.unit,
          target,
          progress: nextProgress,
          completion: nextCompletion,
          updatedAt: new Date().toISOString(),
        },
      };

      const nextScore = computeLocalHealthScore(nextHabits);

      const nextTimeline = [
        {
          habitKey,
          label: extra.label || `${habitMeta.title} updated`,
          action: habitKey === "weight" ? "set" : "log",
          value: numericValue || 1,
          status: "done",
          timestamp: new Date().toISOString(),
        },
        ...(Array.isArray(current.timeline) ? current.timeline : []),
      ].slice(0, 20);

      const nextMood = extra.mood
        ? { label: extra.mood.label || "", emoji: extra.mood.emoji || "" }
        : current.mood;

      const nextWeight = extra.weight !== undefined
        ? Number(extra.weight)
        : current.weight;

      return {
        ...current,
        habits: {
          ...currentHabits,
          ...nextHabits,
        },
        timeline: nextTimeline,
        mood: nextMood,
        weight: nextWeight,
        healthScore: {
          ...(current.healthScore || {}),
          value: nextScore.value,
          level: nextScore.level,
        },
      };
    });

    setSelectedHabits((prev) => (prev.includes(habitKey) ? prev : [...prev, habitKey]));
    setStats(null);
    setAchievements(null);
  };

  const startTracking = async () => {
    if (habitTrackerUnavailable) {
      writeHabitTrackerDraft(selectedHabits, reminderSettings);
      setDrawerOpen(false);
      setActiveStep("dashboard");
      return;
    }

    setSavingConfig(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      await api.put("/api/habit-tracker/config", {
        selectedHabits,
        reminderSettings,
        timezone,
      });
      writeHabitTrackerDraft(selectedHabits, reminderSettings);
      setDrawerOpen(false);
      setActiveStep("dashboard");
      await fetchDashboard();
    } catch (error) {
      if (error?.response?.status === 404) {
        setHabitTrackerUnavailable(true);
        writeHabitTrackerDraft(selectedHabits, reminderSettings);
        setDrawerOpen(false);
        setActiveStep("dashboard");
      }
    } finally {
      setSavingConfig(false);
    }
  };

  const logHabit = async (habitKey, value = 1, extra = {}) => {
    if (habitTrackerUnavailable) {
      applyLocalHabitUpdate(habitKey, value, extra);
      return;
    }

    try {
      const res = await api.post("/api/habit-tracker/log", {
        habitKey,
        value,
        ...extra,
      });

      const entry = res?.data?.entry;
      if (!entry) return;

      const nextScore = Number(entry?.healthScore?.value || 0);
      if (nextScore >= 100) {
        setShowConfetti(true);
        window.setTimeout(() => setShowConfetti(false), 2200);
      }

      setDashboard((prev) => ({
        ...(prev || {}),
        habits: (() => {
          const merged = mergeHabitMaps(prev?.habits || {}, entry.habits || {});
          return merged;
        })(),
        timeline: [...(entry.timeline || []), ...(prev?.timeline || [])].slice(0, 20),
        mood: entry.mood || prev?.mood,
        weight: entry.weight ?? prev?.weight,
        healthScore: (() => {
          const merged = mergeHabitMaps(prev?.habits || {}, entry.habits || {});
          return computeLocalHealthScore(merged);
        })(),
      }));
    } catch (error) {
      if (error?.response?.status === 404) {
        setHabitTrackerUnavailable(true);
        applyLocalHabitUpdate(habitKey, value, extra);
      }
    }
  };

  const runQuickAction = async (habitKey, payload = {}) => {
    // Optimistic local update first so UI remains stable even if network/backend is flaky.
    applyLocalHabitUpdate(habitKey, payload.value || 1, payload);

    if (habitTrackerUnavailable) {
      return;
    }

    try {
      const res = await api.post("/api/habit-tracker/quick-action", {
        habitKey,
        ...payload,
      });
      const entry = res?.data?.entry;
      if (!entry) return;

      const nextScore = Number(entry?.healthScore?.value || 0);
      if (nextScore >= 100) {
        setShowConfetti(true);
        window.setTimeout(() => setShowConfetti(false), 2200);
      }

      const motivation = res?.data?.motivation;
      setDashboard((prev) => ({
        ...(prev || {}),
        habits: (() => {
          const merged = mergeHabitMaps(prev?.habits || {}, entry.habits || {});
          return merged;
        })(),
        timeline: [...(entry.timeline || []), ...(prev?.timeline || [])].slice(0, 20),
        mood: entry.mood || prev?.mood,
        weight: entry.weight ?? prev?.weight,
        healthScore: (() => {
          const merged = mergeHabitMaps(prev?.habits || {}, entry.habits || {});
          return computeLocalHealthScore(merged);
        })(),
        motivationalMessages: motivation
          ? [{ habitKey, message: motivation }, ...(prev?.motivationalMessages || [])].slice(0, 5)
          : prev?.motivationalMessages || [],
      }));
    } catch (error) {
      if (error?.response?.status === 404) {
        setHabitTrackerUnavailable(true);
        return;
      }
      // Keep optimistic state on transient errors; avoid double-counting with retry calls.
    }
  };

  const markMedicationTaken = async () => {
    try {
      const res = await api.get("/api/reminders/today");
      const reminders = Array.isArray(res?.data?.reminders) ? res.data.reminders : [];
      const medication = reminders.find((item) => item.type === "medication" && item.scheduledTimes?.some((d) => !d.taken));
      if (!medication) {
        await logHabit("medication", 1, { label: "Medication logged" });
        return;
      }

      const index = medication.scheduledTimes.findIndex((dose) => !dose.taken);
      if (index >= 0) {
        await api.post("/api/reminders/mark-taken", {
          medicationId: medication.medicationId,
          timeIndex: index,
        });
      }

      await logHabit("medication", 1, { label: "Medication taken" });
    } catch (_error) {
      await logHabit("medication", 1, { label: "Medication logged" });
    }
  };

  const dashboardHabits = dashboard?.habits || {};
  const scoreBreakdown = useMemo(() => {
    return Object.entries(LOCAL_SCORE_WEIGHTS).map(([habitKey, weight]) => {
      const habitMeta = HABITS.find((item) => item.key === habitKey);
      const state = dashboardHabits?.[habitKey] || {};
      const target = Number(state?.target || habitMeta?.target || 0);
      const progress = Number(state?.progress || 0);
      const ratio = target > 0 ? Math.min(1, progress / target) : 0;
      const points = Math.round(ratio * weight);

      return {
        key: habitKey,
        label: habitMeta?.title || habitKey,
        points,
        weight,
      };
    });
  }, [dashboardHabits]);
  const todayHealthScore = useMemo(
    () => scoreBreakdown.reduce((sum, item) => sum + Number(item.points || 0), 0),
    [scoreBreakdown]
  );
  const scoreEquation = useMemo(
    () => `${scoreBreakdown.map((item) => item.points).join(" + ")} = ${todayHealthScore}`,
    [scoreBreakdown, todayHealthScore]
  );
  const timeline = Array.isArray(dashboard?.timeline) ? dashboard.timeline.slice(0, 7) : [];
  const backendNudges = Array.isArray(dashboard?.motivationalMessages)
    ? dashboard.motivationalMessages.map((item) => item?.message).filter(Boolean)
    : [];
  const nudges = (backendNudges.length
    ? backendNudges
    : dashboardHabitItems.map((habit) => MOTIVATIONAL_MESSAGES[habit.key]).filter(Boolean)
  ).slice(0, 3);
  const resolvedStats = useMemo(() => {
    if (stats?.habits?.length) return stats;
    return buildLocalStats(statsRange, selectedHabits, dashboardHabits);
  }, [stats, statsRange, selectedHabits, dashboardHabits]);
  const resolvedAchievements = useMemo(() => {
    if (achievements?.badges?.length) return achievements;
    return buildLocalAchievements(todayHealthScore, timeline.length);
  }, [achievements, todayHealthScore, timeline.length]);

  return (
    <div className="habit-page">
      <div className="habit-topbar">
    

           <header>
                <FeatureHeader
                  title="Daily Health Tracker"
                  subtitle="Keep track of healthier routines"
                  onBack={() => navigate(-1)}
                  rightSlot={(
                             <button
                               onClick={openSetupDrawer}
                               className="symptom-icon-btn"
                               aria-label="Show saved symptom checks"
                               title="Saved symptom checks"
                               style={{ marginRight: 0 }}
                             >
                               <Plus size={18} />
                             </button>
                           )}
                  subtitleStyle={{ color: "color-mix(in srgb, var(--on-bg) 70%, transparent)" }}
                />
              </header>

        <div className="habit-tabs">
          {MAIN_TABS.map((tab) => (
            <button
              key={tab.key}
              className={`habit-tab ${activeStep === tab.key ? "active" : ""}`}
              onClick={() => setActiveStep(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="habit-shell">
        <AnimatePresence mode="wait">
          {loadingConfig ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <SkeletonBlock height={120} className="mb-3" />
              <SkeletonBlock height={140} className="mb-3" />
              <SkeletonBlock height={140} className="mb-3" />
            </motion.div>
          ) : null}

          {!loadingConfig && activeStep === "dashboard" ? (
            <motion.section
              key="dashboard"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
            >
              {loadingDashboard || !dashboard ? (
                <>
                  <SkeletonBlock height={230} className="mb-3" />
                  <SkeletonBlock height={160} className="mb-3" />
                  <SkeletonBlock height={180} className="mb-3" />
                </>
              ) : (
                <>
                  <div className="habit-glass-card p-4 mb-3">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <p className="mb-0" style={{ fontWeight: 700, fontSize: "1.15rem" }}>{greeting}, {userName}</p>
                        <p className="mb-0" style={{ color: "#7f7396", fontSize: "0.82rem" }}>
                          {new Date().toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })}
                        </p>
                      </div>

                      <div className="habit-score-tag">{scoreLabel(todayHealthScore)}</div>
                    </div>

                    <ProgressRing value={todayHealthScore} />

                    <p className="text-center mb-0 mt-2" style={{ color: "#7f7396", fontSize: "0.84rem" }}>
                      Daily Health Score
                    </p>

                    <div className="mt-3" style={{ borderTop: "1px solid #e7e0f3", paddingTop: 10 }}>
                      <p className="mb-2" style={{ color: "#7f7396", fontSize: "0.75rem", fontWeight: 700 }}>
                        Score Breakdown
                      </p>
                      <div className="d-flex flex-wrap gap-2">
                        {scoreBreakdown.map((item) => (
                          <span
                            key={item.key}
                            style={{
                              fontSize: "0.72rem",
                              padding: "4px 8px",
                              borderRadius: 999,
                              background: "rgba(127, 0, 255, 0.08)",
                              color: "#5e4e7e",
                              fontWeight: 600,
                            }}
                          >
                            {item.label}: {item.points}/{item.weight}
                          </span>
                        ))}
                      </div>
                      
                    </div>
                  </div>

                  <div className="habit-dashboard-grid mb-3">
                    {dashboardHabitItems.map((habit) => {
                      const Icon = habit.icon;
                      const state = dashboardHabits[habit.key] || {};
                      const progress = Number(state.progress || 0);
                      const target = Number(state.target || habit.target);
                      const percent = Number(state.completion || toPercent(progress, target));

                      return (
                        <motion.div key={habit.key} className="habit-card p-3" whileHover={{ y: -2 }}>
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="d-flex align-items-center gap-2">
                              <div className="habit-choice-icon mb-0" style={{ width: 38, height: 38, borderRadius: 12 }}>
                                <Icon size={18} />
                              </div>
                              <div>
                                <p className="mb-0" style={{ fontWeight: 700, fontSize: "0.86rem" }}>{habit.title}</p>
                                <p className="mb-0" style={{ color: "#7f7396", fontSize: "0.73rem" }}>
                                  {progress} / {target} {habit.unit}
                                </p>
                              </div>
                            </div>
                            <span style={{ fontSize: "0.78rem", color: "#7f7396", fontWeight: 700 }}>{percent}%</span>
                          </div>

                          <div className="habit-mini-progress mb-2">
                            <motion.span initial={{ width: 0 }} animate={{ width: `${percent}%` }} transition={{ duration: 0.6 }} />
                          </div>

                          <button
                            className="habit-quick-btn w-100"
                            onClick={() => {
                              if (habit.key === "medication") {
                                runQuickAction("medication", { label: "Medication taken" });
                                return;
                              }
                              if (habit.key === "mood") {
                                runQuickAction(habit.key, { value: 1, mood: MOODS[0], label: "Mood logged" });
                                return;
                              }
                              if (habit.key === "weight") {
                                runQuickAction(habit.key, { value: 1, weight: 72, label: "Weight updated" });
                                return;
                              }
                              runQuickAction(habit.key, {
                                value: habit.quickValue || 1,
                                label: `${habit.title} updated`,
                              });
                            }}
                          >
                            + {habit.title}
                          </button>
                        </motion.div>
                      );
                    })}
                  </div>

                  <div className="habit-card p-3 mb-3">
                    <p className="habit-card-title mb-2">Quick Actions</p>
                    <div className="habit-quick-actions">
                      <button className="habit-quick-btn" onClick={() => runQuickAction("water", { value: 1, label: "Water logged" })}>+ Drink Water</button>
                      <button className="habit-quick-btn" onClick={() => runQuickAction("medication", { value: 1, label: "Medication taken" })}>+ Medication</button>
                      <button className="habit-quick-btn" onClick={() => runQuickAction("exercise", { value: 15, label: "Exercise logged" })}>+ Exercise</button>
                      <button className="habit-quick-btn" onClick={() => runQuickAction("walking", { value: 1000, label: "Steps logged" })}>+ Walk</button>
                      <button className="habit-quick-btn" onClick={() => runQuickAction("mood", { value: 1, mood: MOODS[0], label: "Mood logged" })}>+ Mood</button>
                      <button className="habit-quick-btn" onClick={() => runQuickAction("weight", { value: 1, weight: 72, label: "Weight updated" })}>+ Weight</button>
                    </div>
                  </div>

                  <div className="habit-card p-3 mb-3">
                    <p className="habit-card-title mb-2">Today's Timeline</p>
                    <div className="habit-timeline">
                      {timeline.length ? (
                        timeline.map((event, index) => (
                          <div className="habit-event" key={`${event.habitKey}-${index}`}>
                            <p className="mb-0" style={{ fontSize: "0.74rem", color: "#7f7396" }}>
                              {new Date(event.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </p>
                            <p className="mb-0" style={{ fontWeight: 600, fontSize: "0.85rem" }}>{event.label || "Habit updated"}</p>
                          </div>
                        ))
                      ) : (
                        <p className="mb-0" style={{ color: "#7f7396", fontSize: "0.84rem" }}>No events yet today.</p>
                      )}
                    </div>
                  </div>

                  <motion.div className="habit-card p-3 mb-3" initial={{ scale: 0.98 }} animate={{ scale: 1 }}>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <p className="habit-card-title mb-0">Qureo's Insight</p>
                      <Sparkles size={16} color="#7f00ff" />
                    </div>
                    <div
                      style={{
                        borderRadius: 18,
                        padding: 14,
                        color: "#fff",
                        background: "linear-gradient(135deg, #9f45ff, #6f00ff)",
                      }}
                    >
                      <p className="mb-0" style={{ fontSize: "0.88rem", lineHeight: 1.5 }}>
                        {dashboard.ashaInsight}
                      </p>
                    </div>
                  </motion.div>

                  <div className="habit-card p-3 mb-3">
                    <p className="habit-card-title mb-2">Upcoming Reminders</p>
                    {Array.isArray(dashboard.upcomingReminders) && dashboard.upcomingReminders.length ? (
                      dashboard.upcomingReminders.slice(0, 4).map((item) => (
                        <div key={`${item.habitKey}-${item.time}`} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                          <p className="mb-0" style={{ fontWeight: 600, fontSize: "0.84rem" }}>{item.title}</p>
                          <span style={{ color: "#7f7396", fontSize: "0.78rem" }}>{formatTime(item.time)}</span>
                        </div>
                      ))
                    ) : (
                      <p className="mb-0" style={{ color: "#7f7396", fontSize: "0.84rem" }}>No reminders scheduled yet.</p>
                    )}
                  </div>

                  <div className="habit-card p-3">
                    <p className="habit-card-title mb-2">Motivational Notifications</p>
                    {nudges.length ? (
                      nudges.map((item, index) => (
                        <p key={`${item}-${index}`} className="mb-2" style={{ color: "#6a6180", fontSize: "0.82rem" }}>
                          {item}
                        </p>
                      ))
                    ) : (
                      <p className="mb-0" style={{ color: "#7f7396", fontSize: "0.84rem" }}>Select habits to receive smart nudges.</p>
                    )}
                  </div>
                </>
              )}
            </motion.section>
          ) : null}

          {!loadingConfig && activeStep === "statistics" ? (
            <motion.section
              key="statistics"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
            >
              <div className="habit-card p-4 mb-3">
                <h2 className="habit-section-title">Statistics</h2>
                <p className="habit-section-subtitle">
                  Track completion, averages, streaks, and missed days.
                </p>

                <div className="habit-chip-group mt-3">
                  {RANGE_TABS.map((range) => (
                    <button
                      key={range}
                      className={`habit-chip ${statsRange === range ? "active" : ""}`}
                      onClick={() => setStatsRange(range)}
                    >
                      {range[0].toUpperCase() + range.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {loadingStats ? (
                <>
                  <SkeletonBlock height={130} className="mb-3" />
                  <SkeletonBlock height={130} className="mb-3" />
                </>
              ) : null}

              {!loadingStats && resolvedStats?.habits?.length ? (
                resolvedStats.habits.map((habit) => (
                  <motion.div key={habit.key} className="habit-card p-3 mb-3" layout>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <p className="habit-card-title mb-0">{habit.title}</p>
                      <span style={{ color: "#7f7396", fontSize: "0.78rem" }}>
                        {habit.completionPercentage}% complete
                      </span>
                    </div>

                    <div className="habit-mini-progress mb-3">
                      <motion.span
                        initial={{ width: 0 }}
                        animate={{ width: `${habit.completionPercentage}%` }}
                        transition={{ duration: 0.7 }}
                      />
                    </div>

                    <div className="row g-2">
                      <div className="col-6 col-md-3">
                        <div className="habit-glass-card p-2 text-center">
                          <p className="mb-0" style={{ fontSize: "0.72rem", color: "#7f7396" }}>Daily Avg</p>
                          <p className="mb-0" style={{ fontWeight: 700 }}>{habit.dailyAverage}%</p>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="habit-glass-card p-2 text-center">
                          <p className="mb-0" style={{ fontSize: "0.72rem", color: "#7f7396" }}>Longest Streak</p>
                          <p className="mb-0" style={{ fontWeight: 700 }}>{habit.longestStreak}d</p>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="habit-glass-card p-2 text-center">
                          <p className="mb-0" style={{ fontSize: "0.72rem", color: "#7f7396" }}>Current</p>
                          <p className="mb-0" style={{ fontWeight: 700 }}>{habit.currentStreak}d</p>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="habit-glass-card p-2 text-center">
                          <p className="mb-0" style={{ fontSize: "0.72rem", color: "#7f7396" }}>Missed</p>
                          <p className="mb-0" style={{ fontWeight: 700 }}>{habit.missedDays}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : null}

              {!loadingStats && !resolvedStats?.habits?.length ? (
                <div className="habit-card p-3">
                  <p className="mb-0" style={{ color: "#7f7396" }}>
                    Start logging habits to view your analytics.
                  </p>
                </div>
              ) : null}
            </motion.section>
          ) : null}

          {!loadingConfig && activeStep === "achievements" ? (
            <motion.section
              key="achievements"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
            >
              <div className="habit-card p-4 mb-3">
                <h2 className="habit-section-title">Achievements</h2>
                <p className="habit-section-subtitle">
                  Unlock streak badges as you maintain daily momentum.
                </p>
              </div>

              {loadingAchievements ? (
                <>
                  <SkeletonBlock height={120} className="mb-3" />
                  <SkeletonBlock height={120} className="mb-3" />
                </>
              ) : null}

              {!loadingAchievements && resolvedAchievements?.badges?.length ? (
                <div className="row g-3">
                  {resolvedAchievements.badges.map((badge) => {
                    const Icon = badge.id.includes("streak") || badge.id === "first-day" ? Flame : badge.id === "gold" ? Trophy : Medal;
                    return (
                      <div className="col-6 col-md-4" key={badge.id}>
                        <motion.div
                          className={`habit-card habit-badge-card ${badge.unlocked ? "" : "locked"}`}
                          whileHover={{ y: -3 }}
                          animate={badge.unlocked ? { scale: [1, 1.03, 1] } : { scale: 1 }}
                          transition={{ duration: 0.4 }}
                        >
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 16,
                              margin: "0 auto 10px",
                              display: "grid",
                              placeItems: "center",
                              background: "rgba(127, 0, 255, 0.12)",
                              color: "#7f00ff",
                            }}
                          >
                            <Icon size={22} />
                          </div>

                          <p className="mb-1" style={{ fontWeight: 700, fontSize: "0.83rem" }}>{badge.title}</p>
                          <p className="mb-2" style={{ color: "#7f7396", fontSize: "0.72rem" }}>
                            {badge.progress}/{badge.target}
                          </p>

                          <div className="habit-badge-progress">
                            <span style={{ width: `${badge.percentage}%` }} />
                          </div>
                        </motion.div>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {!loadingAchievements && !resolvedAchievements?.badges?.length ? (
                <div className="habit-card p-3">
                  <p className="mb-0" style={{ color: "#7f7396" }}>
                    Complete your first tracked day to unlock achievements.
                  </p>
                </div>
              ) : null}

              {!!recentTips.length ? (
                <div className="habit-card p-3 mt-3">
                  <p className="habit-card-title mb-2">Recent AI Tips</p>
                  {recentTips.slice(0, 2).map((tip) => (
                    <p key={tip._id || tip.id} className="mb-2" style={{ color: "#6f6784", fontSize: "0.82rem" }}>
                      {tip.content}
                    </p>
                  ))}
                </div>
              ) : null}
            </motion.section>
          ) : null}
        </AnimatePresence>
      </div>

      {/* Setup drawer (habits + reminders) */}
      <AnimatePresence>
        {drawerOpen ? (
          <>
            <motion.div
              key="drawer-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDrawerOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15, 10, 30, 0.45)",
                zIndex: 200,
              }}
            />
            <motion.div
              key="drawer-panel"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 260 }}
              style={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 201,
                background: "#faf8ff",
                borderRadius: "28px 28px 0 0",
                maxHeight: "88vh",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Drawer handle */}
              <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
                <div style={{ width: 44, height: 5, borderRadius: 999, background: "#dcd5f2" }} />
              </div>

              {/* Drawer header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 20px 12px" }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: "1.05rem" }}>
                  {drawerStep === "selection" ? "Choose Habits" : "Set Reminders"}
                </p>
                <button
                  onClick={() => setDrawerOpen(false)}
                  style={{ border: "none", background: "transparent", fontSize: "1.3rem", color: "#9e94b5", cursor: "pointer" }}
                >
                  ✕
                </button>
              </div>

              {/* Drawer sub-tabs */}
              <div style={{ display: "flex", gap: 8, padding: "0 20px 12px" }}>
                {[{ key: "selection", label: "1. Habits" }, { key: "reminders", label: "2. Reminders" }].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setDrawerStep(s.key)}
                    style={{
                      flex: 1,
                      padding: "9px 0",
                      borderRadius: 14,
                      border: "1px solid",
                      borderColor: drawerStep === s.key ? "transparent" : "#e8e2f5",
                      background: drawerStep === s.key ? "linear-gradient(140deg,#8d2cff,#6f00ff)" : "#fff",
                      color: drawerStep === s.key ? "#fff" : "#746b8a",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Scrollable content */}
              <div style={{ overflowY: "auto", flex: 1, padding: "0 16px 120px" }}>

                {/* Habit selection inside drawer */}
                {drawerStep === "selection" ? (
                  <div className="habit-selection-grid">
                    {HABITS.map((habit) => {
                      const Icon = habit.icon;
                      const selected = selectedHabits.includes(habit.key);
                      return (
                        <motion.button
                          key={habit.key}
                          className={`habit-card habit-choice text-start ${selected ? "selected" : ""}`}
                          whileHover={{ y: -3, scale: 1.01 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => toggleHabit(habit.key)}
                        >
                          {selected ? <span className="habit-choice-badge"><Check size={14} /></span> : null}
                          <div className="habit-choice-icon"><Icon size={25} /></div>
                          <p className="habit-choice-title">{habit.title}</p>
                          <p className="habit-choice-desc">{habit.description}</p>
                        </motion.button>
                      );
                    })}
                  </div>
                ) : null}

                {/* Reminder config inside drawer */}
                {drawerStep === "reminders" ? selectedHabitItems.map((habit) => {
                  const Icon = habit.icon;
                  const settings = reminderSettings[habit.key] || defaultReminderFor(habit.key);

                  return (
                    <motion.div key={habit.key} className="habit-card habit-reminder-card" layout>
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="habit-choice-icon mb-0" style={{ width: 42, height: 42, borderRadius: 14 }}>
                            <Icon size={20} />
                          </div>
                          <div>
                            <p className="mb-0" style={{ fontWeight: 700 }}>{habit.title}</p>
                            <p className="mb-0" style={{ color: "#7f7396", fontSize: "0.76rem" }}>Daily target: {habit.target} {habit.unit}</p>
                          </div>
                        </div>
                        <div className="form-check form-switch m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={settings.enabled !== false}
                            onChange={(e) => updateReminder(habit.key, "enabled", e.target.checked)}
                          />
                        </div>
                      </div>

                      <div className="row g-2">
                        <div className="col-6">
                          <label className="form-label mb-1" style={{ fontSize: "0.76rem", color: "#7f7396" }}>Time</label>
                          <input className="form-control" type="time" value={settings.time || "08:00"} onChange={(e) => updateReminder(habit.key, "time", e.target.value)} />
                        </div>
                        <div className="col-6">
                          <label className="form-label mb-1" style={{ fontSize: "0.76rem", color: "#7f7396" }}>Repeat</label>
                          <select className="form-select" value={settings.repeat || "Daily"} onChange={(e) => updateReminder(habit.key, "repeat", e.target.value)}>
                            {REPEAT_OPTIONS.map((item) => <option key={item}>{item}</option>)}
                          </select>
                        </div>
                        {habit.key === "water" ? (
                          <div className="col-12">
                            <label className="form-label mb-1" style={{ fontSize: "0.76rem", color: "#7f7396" }}>Reminder Every</label>
                            <div className="habit-chip-group">
                              {["30 mins", "1 hour", "2 hours", "3 hours", "4 hours"].map((item) => (
                                <button key={item} className={`habit-chip ${settings.interval === item ? "active" : ""}`} onClick={() => updateReminder(habit.key, "interval", item)}>{item}</button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        {habit.key === "medication" ? (
                          <div className="col-12">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                              <label className="form-label mb-0" style={{ fontSize: "0.76rem", color: "#7f7396" }}>Doses</label>
                              <button className="habit-chip" onClick={addMedicationSlot}><Plus size={12} /> Add Dose</button>
                            </div>
                            <div className="row g-2">
                              {(settings.slots || [settings.time || "08:00"]).map((slot, index) => (
                                <div className="col-6" key={`${habit.key}-slot-${index}`}>
                                  <div className="d-flex gap-1 align-items-center">
                                    <input className="form-control" type="time" value={slot} onChange={(e) => updateMedicationSlot(index, e.target.value)} />
                                    <button className="habit-chip" onClick={() => removeMedicationSlot(index)} disabled={(settings.slots || []).length <= 1}>x</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        {habit.key === "sleep" ? (
                          <div className="col-12">
                            <div className="row g-2">
                              <div className="col-12">
                                <div className="d-flex align-items-center justify-content-between">
                                  <label className="form-label mb-0" style={{ fontSize: "0.76rem", color: "#7f7396" }}>
                                    Sleep Now Trigger
                                  </label>
                                  <div className="form-check form-switch m-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={settings.sleepReminderEnabled !== false}
                                      onChange={(e) => updateReminder(habit.key, "sleepReminderEnabled", e.target.checked)}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <input
                                  className="form-control"
                                  type="time"
                                  value={settings.sleepTime || "22:00"}
                                  onChange={(e) => updateReminder(habit.key, "sleepTime", e.target.value)}
                                />
                              </div>

                              <div className="col-12 mt-2">
                                <div className="d-flex align-items-center justify-content-between">
                                  <label className="form-label mb-0" style={{ fontSize: "0.76rem", color: "#7f7396" }}>
                                    Wake Now Trigger
                                  </label>
                                  <div className="form-check form-switch m-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={settings.wakeReminderEnabled !== false}
                                      onChange={(e) => updateReminder(habit.key, "wakeReminderEnabled", e.target.checked)}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <input
                                  className="form-control"
                                  type="time"
                                  value={settings.wakeTime || "06:30"}
                                  onChange={(e) => updateReminder(habit.key, "wakeTime", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        ) : null}
                        {settings.repeat === "Custom" ? (
                          <div className="col-12">
                            <div className="habit-chip-group">
                              {CUSTOM_DAYS.map((day) => (
                                <button key={day} className={`habit-chip ${settings.customDays?.includes(day) ? "active" : ""}`} onClick={() => toggleCustomDay(habit.key, day)}>{day}</button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </motion.div>
                  );
                }) : null}
              </div>

              {/* Drawer action button */}
              <div style={{
                position: "absolute",
                bottom: "env(safe-area-inset-bottom, 16px)",
                left: 16,
                right: 16,
                paddingBottom: 16,
              }}>
                {drawerStep === "selection" ? (
                  <button
                    className="habit-cta-btn"
                    disabled={!selectedHabits.length}
                    onClick={goToReminderSetup}
                  >
                    Continue ({selectedHabits.length} habits)
                  </button>
                ) : (
                  <button className="habit-cta-btn" onClick={startTracking} disabled={savingConfig}>
                    {savingConfig ? "Saving..." : "Save & Start Tracking"}
                  </button>
                )}
              </div>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>

      {showConfetti ? (
        <div className="habit-confetti">
          {confettiPieces.map((piece) => (
            <i
              key={piece.id}
              style={{
                left: piece.left,
                background: piece.background,
                animationDelay: piece.delay,
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
